import { ProductMixReportRow, ItemAggregate, GroupAggregate, Totals, PivotResult } from "./types";

export const emptyTotals = (): Totals => ({
  totalUnitsSold: 0,
  totalNetCents: 0,
  totalGrossCents: 0,
  totalTaxCents: 0,
  totalDiscountsCents: 0,
  totalPromoDiscountsCents: 0,
  totalRefundsCents: 0,
  cashUnitsSold: 0,
  cashBalanceUnitsSold: 0,
  creditUnitsSold: 0,
  rfidUnitsSold: 0,
  tokenUnitsSold: 0,
  grossCashCents: 0,
  grossCashBalanceCents: 0,
  grossCreditCents: 0,
  grossRfidCents: 0,
  grossPromoBalanceCents: 0,
  grossTokenCents: 0,
});

const grossCents = (r: ProductMixReportRow) =>
  r.gross_cash_balance_sales +
  r.gross_cash_sales +
  r.gross_credit_sales +
  r.gross_promo_balance_sales +
  r.gross_rfid_sales +
  r.gross_token_sales;

// Net = gross - (tokens + refunds + promo_balance + discounts + promo_discounts + tax). Matches legacy formula.
const netCents = (r: ProductMixReportRow) =>
  grossCents(r) -
  (r.gross_token_sales +
    r.total_refunds +
    r.gross_promo_balance_sales +
    r.total_discounts +
    r.total_promo_discounts +
    r.total_tax);

const addRowToItem = (agg: ItemAggregate, r: ProductMixReportRow) => {
  agg.totalUnitsSold += r.total_units_sold;
  agg.cashUnitsSold += r.cash_units_sold;
  agg.cashBalanceUnitsSold += r.cash_balance_units_sold;
  agg.creditUnitsSold += r.credit_units_sold;
  agg.rfidUnitsSold += r.rfid_units_sold;
  agg.tokenUnitsSold += r.token_units_sold;
  agg.grossCashCents += r.gross_cash_sales;
  agg.grossCashBalanceCents += r.gross_cash_balance_sales;
  agg.grossCreditCents += r.gross_credit_sales;
  agg.grossRfidCents += r.gross_rfid_sales;
  agg.grossPromoBalanceCents += r.gross_promo_balance_sales;
  agg.grossTokenCents += r.gross_token_sales;
  agg.totalGrossCents += grossCents(r);
  agg.totalNetCents += netCents(r);
  agg.totalTaxCents += r.total_tax;
  agg.totalDiscountsCents += r.total_discounts;
  agg.totalPromoDiscountsCents += r.total_promo_discounts;
  agg.totalRefundsCents += r.total_refunds;
};

const newItemAggregate = (r: ProductMixReportRow, key: string): ItemAggregate => ({
  key,
  posName: r.pos_name,
  description: r.description ?? "",
  vendorName: r.vendor_name ?? "",
  productType: r.product_type || "Uncategorized",
  upc: r.upc ?? "",
  glAccountName: r.gl_account_name ?? "",
  glAccountNumber: r.gl_account_number ?? "",
  itemPriceCents: r.item_price,
  totalUnitsSold: 0,
  cashUnitsSold: 0,
  cashBalanceUnitsSold: 0,
  creditUnitsSold: 0,
  rfidUnitsSold: 0,
  tokenUnitsSold: 0,
  grossCashCents: 0,
  grossCashBalanceCents: 0,
  grossCreditCents: 0,
  grossRfidCents: 0,
  grossPromoBalanceCents: 0,
  grossTokenCents: 0,
  totalGrossCents: 0,
  totalNetCents: 0,
  totalTaxCents: 0,
  totalDiscountsCents: 0,
  totalPromoDiscountsCents: 0,
  totalRefundsCents: 0,
});

const itemKey = (r: ProductMixReportRow) =>
  `${r.pos_name}||${r.description ?? ""}||${r.vendor_name ?? ""}`;

const sumIntoTotals = (totals: Totals, item: ItemAggregate) => {
  totals.totalUnitsSold += item.totalUnitsSold;
  totals.totalNetCents += item.totalNetCents;
  totals.totalGrossCents += item.totalGrossCents;
  totals.totalTaxCents += item.totalTaxCents;
  totals.totalDiscountsCents += item.totalDiscountsCents;
  totals.totalPromoDiscountsCents += item.totalPromoDiscountsCents;
  totals.totalRefundsCents += item.totalRefundsCents;
  totals.cashUnitsSold += item.cashUnitsSold;
  totals.cashBalanceUnitsSold += item.cashBalanceUnitsSold;
  totals.creditUnitsSold += item.creditUnitsSold;
  totals.rfidUnitsSold += item.rfidUnitsSold;
  totals.tokenUnitsSold += item.tokenUnitsSold;
  totals.grossCashCents += item.grossCashCents;
  totals.grossCashBalanceCents += item.grossCashBalanceCents;
  totals.grossCreditCents += item.grossCreditCents;
  totals.grossRfidCents += item.grossRfidCents;
  totals.grossPromoBalanceCents += item.grossPromoBalanceCents;
  totals.grossTokenCents += item.grossTokenCents;
};

export const aggregateByItem = (rows: ProductMixReportRow[]): { items: ItemAggregate[]; totals: Totals } => {
  const map = new Map<string, ItemAggregate>();
  const totals = emptyTotals();
  for (const r of rows) {
    const key = itemKey(r);
    let agg = map.get(key);
    if (!agg) {
      agg = newItemAggregate(r, key);
      map.set(key, agg);
    }
    addRowToItem(agg, r);
  }
  const items = [...map.values()].sort((a, b) => b.totalNetCents - a.totalNetCents);
  items.forEach((i) => sumIntoTotals(totals, i));
  return { items, totals };
};

const formatIsoDate = (iso: string | undefined): string => {
  if (!iso) return "Unknown";
  return iso.split("T")[0];
};

const prettyDate = (iso: string): string => {
  if (!iso || iso === "Unknown") return "Unknown date";
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
};

export const aggregateByDay = (rows: ProductMixReportRow[]): { groups: GroupAggregate[]; totals: Totals } => {
  const byDay = new Map<string, ProductMixReportRow[]>();
  for (const r of rows) {
    const day = formatIsoDate(r.transaction_date);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(r);
  }
  const groups: GroupAggregate[] = [...byDay.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([day, dayRows]) => {
      const { items, totals } = aggregateByItem(dayRows);
      return { key: `day:${day}`, label: prettyDate(day), subLabel: day, items, totals };
    });
  const totals = emptyTotals();
  groups.forEach((g) => Object.keys(g.totals).forEach((k) => ((totals as any)[k] += (g.totals as any)[k])));
  return { groups, totals };
};

export const aggregateByLocation = (rows: ProductMixReportRow[]): { groups: GroupAggregate[]; totals: Totals } => {
  const byLoc = new Map<string, ProductMixReportRow[]>();
  for (const r of rows) {
    const loc = r.location_name || "Unknown";
    if (!byLoc.has(loc)) byLoc.set(loc, []);
    byLoc.get(loc)!.push(r);
  }
  const groups: GroupAggregate[] = [...byLoc.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([loc, locRows]) => {
      const { items, totals } = aggregateByItem(locRows);
      return { key: `loc:${loc}`, label: loc, items, totals };
    });
  const totals = emptyTotals();
  groups.forEach((g) => Object.keys(g.totals).forEach((k) => ((totals as any)[k] += (g.totals as any)[k])));
  return { groups, totals };
};

export const aggregateByDayByLocation = (
  rows: ProductMixReportRow[]
): { groups: GroupAggregate[]; totals: Totals } => {
  const byDay = new Map<string, ProductMixReportRow[]>();
  for (const r of rows) {
    const day = formatIsoDate(r.transaction_date);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(r);
  }
  const groups: GroupAggregate[] = [...byDay.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([day, dayRows]) => {
      const { groups: locGroups, totals } = aggregateByLocation(dayRows);
      return {
        key: `day:${day}`,
        label: prettyDate(day),
        subLabel: day,
        items: [],
        subgroups: locGroups,
        totals,
      };
    });
  const totals = emptyTotals();
  groups.forEach((g) => Object.keys(g.totals).forEach((k) => ((totals as any)[k] += (g.totals as any)[k])));
  return { groups, totals };
};

const formatHourLabel = (h: number): string => {
  if (h === 0) return "12 AM";
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

export const pivotByHour = (rows: ProductMixReportRow[]): PivotResult => {
  return buildPivot(rows, "Hour", (r) => {
    let hour = 0;
    let day = r.transaction_date ?? "";
    if (r.transaction_hour) {
      const d = new Date(r.transaction_hour);
      hour = d.getUTCHours();
      day = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    }
    return { key: `${day}||${hour}`, label: `${prettyDate(day)} · ${formatHourLabel(hour)}`, sort: `${day}||${String(hour).padStart(2, "0")}` };
  });
};

export const pivotByUser = (rows: ProductMixReportRow[]): PivotResult => {
  return buildPivot(rows, "User", (r) => {
    const u = r.username || "Unknown";
    return { key: u, label: u, sort: u.toLowerCase() };
  });
};

const buildPivot = (
  rows: ProductMixReportRow[],
  axisLabel: string,
  rowKeyFn: (r: ProductMixReportRow) => { key: string; label: string; sort: string }
): PivotResult => {
  const itemNamesSet = new Set<string>();
  const buckets = new Map<string, { label: string; sort: string; cells: Record<string, { units: number; grossCents: number }> }>();

  for (const r of rows) {
    itemNamesSet.add(r.pos_name);
    const { key, label, sort } = rowKeyFn(r);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { label, sort, cells: {} };
      buckets.set(key, bucket);
    }
    const cell = (bucket.cells[r.pos_name] ||= { units: 0, grossCents: 0 });
    cell.units += r.total_units_sold;
    cell.grossCents += grossCents(r);
  }

  const itemNames = [...itemNamesSet].sort();
  const itemTotals: Record<string, { units: number; grossCents: number }> = {};
  itemNames.forEach((n) => (itemTotals[n] = { units: 0, grossCents: 0 }));
  const totals = { units: 0, grossCents: 0 };

  for (const b of buckets.values()) {
    for (const name of itemNames) {
      const c = b.cells[name];
      if (!c) continue;
      itemTotals[name].units += c.units;
      itemTotals[name].grossCents += c.grossCents;
      totals.units += c.units;
      totals.grossCents += c.grossCents;
    }
  }

  const sortedRows = [...buckets.entries()]
    .sort((a, b) => b[1].sort.localeCompare(a[1].sort))
    .map(([key, b]) => ({ key, label: b.label, cells: b.cells }));

  return { axisLabel, rows: sortedRows, itemNames, totals, itemTotals };
};

export const usd = (cents: number): string => {
  const v = cents / 100;
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

export const num = (n: number): string => n.toLocaleString("en-US");
